import type { FormSchema, GeneratedFile, GeneratorOptions } from '@cli-types/core-engine/schema.js'
import type { FormAdminPagesResult } from '@cli-types/next-adapter/forms.js'
import { createGeneratedFile } from '@cli-utils/files.js'
import { toCamelCase, toKebabCase, toPascalCase } from '@cli-utils/text.js'
import { getAllFormSchemaFields } from '@core-engine/schema/field-helpers/form-fields/get-all-form-schema-fields.js'
import { hasDynamicFields } from '@core-engine/schema/field-helpers/form-fields/has-dynamic-fields.js'
import { resolveSchemaActionImportPath } from '@next-adapter/config/paths/resolve-schema-action-import-path.js'
import { buildFormDevModeIntegration } from '@next-adapter/generators/dev-mode-integration/build-form-dev-mode-integration.js'
import { buildDeleteDialogSource } from '@next-adapter/generators/row-actions-emitters/build-delete-dialog-source.js'
import { buildRowActionsSource } from '@next-adapter/generators/row-actions-emitters/build-row-actions-source.js'
import { generateColumns } from './admin-columns/generate-columns.js'
import { generatePageContent } from './admin-page-content.js'
import { generatePageSkeleton } from './admin-page/generate-page-skeleton.js'
import { generatePage } from './admin-page/generate-page.js'
import { generateSubmissionDrawer } from './admin-submission-drawer.js'
import { generateTable } from './admin-table.js'

export function generateFormAdminPages(
  schema: FormSchema,
  pagesDir: string,
  options: GeneratorOptions
): FormAdminPagesResult {
  const formName = schema.name
  const kebab = toKebabCase(formName)
  const pascal = toPascalCase(formName)
  const camel = toCamelCase(formName)
  const actionImportPath = resolveSchemaActionImportPath(formName, options.outputNamespace)
  const adminRoutePath = options.adminRoutePath ?? '/admin'
  const devModeIntegration = buildFormDevModeIntegration(
    schema,
    options.adminDir ?? 'admin',
    actionImportPath,
    options.adminImportAlias
  )
  const fields = getAllFormSchemaFields(schema)
  const hasDelete = schema.actions?.delete !== false
  const hasExport = schema.actions?.export === true
  const actions = `${pascal}Actions`
  const deleteDialog = `Delete${pascal}SubmissionDialog`
  const deleteDialogFileName = `delete-${kebab}-submission-dialog`
  const baseFiles: GeneratedFile[] = [
    createGeneratedFile(`${pagesDir}/forms/${kebab}/page.tsx`, generatePage(pascal, kebab)),
    createGeneratedFile(
      `${pagesDir}/forms/${kebab}/${kebab}-submissions-page-skeleton.tsx`,
      generatePageSkeleton(pascal, schema.label)
    ),
    createGeneratedFile(
      `${pagesDir}/forms/${kebab}/columns.tsx`,
      generateColumns(
        fields,
        pascal,
        kebab,
        camel,
        actionImportPath,
        schema.columns,
        schema.actions
      )
    )
  ]
  if (hasDelete) {
    baseFiles.push(
      createGeneratedFile(
        `${pagesDir}/forms/${kebab}/actions.tsx`,
        buildRowActionsSource({
          componentName: actions,
          dialogComponentName: deleteDialog,
          dialogModuleName: deleteDialogFileName,
          noun: 'submission'
        })
      ),
      createGeneratedFile(
        `${pagesDir}/forms/${kebab}/${deleteDialogFileName}.tsx`,
        buildDeleteDialogSource({
          componentName: deleteDialog,
          deleteActionName: `delete${pascal}Submission`,
          actionImportPath,
          noun: 'submission',
          queryKey: `${camel}-submissions`
        })
      )
    )
  }
  return {
    files: [
      ...baseFiles,
      createGeneratedFile(
        `${pagesDir}/forms/${kebab}/${kebab}-submissions-table.tsx`,
        generateTable(pascal, kebab, schema.label, actionImportPath)
      ),
      createGeneratedFile(
        `${pagesDir}/forms/${kebab}/${kebab}-submissions-page-content.tsx`,
        generatePageContent(
          pascal,
          kebab,
          camel,
          schema.label,
          actionImportPath,
          adminRoutePath,
          devModeIntegration.buttonExportName,
          devModeIntegration.buttonImportPath,
          hasExport,
          hasDelete
        )
      ),
      ...devModeIntegration.files,
      createGeneratedFile(
        `${pagesDir}/forms/${kebab}/${kebab}-submission-drawer.tsx`,
        generateSubmissionDrawer(
          pascal,
          kebab,
          fields,
          schema.label,
          hasDynamicFields(schema),
          actionImportPath
        )
      )
    ]
  }
}
